import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  getAuth,
  sendPasswordResetEmail,
  fetchSignInMethodsForEmail,
} from "firebase/auth";
import firebase from "app/Auth";
import { btnDelay, useScale } from "common/utils";
import { Select } from "common/elements/Select/Select";
import parentI18n from "features/shared/i18n";
import { BackBtn } from "./BackBtn";
import { MobileInput } from "./AuthStyles";
import { useAuthLocale } from "./useAuthLocale";

export const PasswordResetScreen = () => {
  const { currentLocale, changeLocale, localeOptions, localeLabels } =
    useAuthLocale();
  const navigate = useNavigate();
  const { width, height, scale } = useScale();
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [localeOpen, setLocaleOpen] = useState(false);
  const langBtnRef = useRef<HTMLButtonElement>(null);

  const handleToggleLocale = () => {
    btnDelay(() => {
      setLocaleOpen(!localeOpen);
    });
  };

  const handleCloseLocale = () => {
    setLocaleOpen(false);
  };

  const handleChangeLocale = (locale: string) => {
    changeLocale(locale);
  };

  const handlePasswordReset = async () => {
    if (!email) {
      setError(parentI18n.t("MSG_SIGN_EMAIL_REQUIRED"));
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError(parentI18n.t("MSG_SIGN_EMAIL_INVALID"));
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const auth = getAuth(firebase);
      const methods = await fetchSignInMethodsForEmail(auth, email);
      if (methods.includes("password")) {
        await sendPasswordResetEmail(auth, email);
      }
      setIsEmailSent(true);
    } catch {
      setError(parentI18n.t("MSG_SIGN_PASSWORD_RESET_FAILED"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate("/auth/email-login");
  };

  return (
    <div className="flex-col-el w-full h-full bg-gray2-30">
      {/* ヘッダー */}
      <div style={{ height: 90 * scale }} className="flex-col-el flex-center">
        <header
          style={{ transform: `scale(${scale})`, width: width / scale }}
          className="flex-row-el h-[90px] items-center px-6 py-4 border-b border-outline bg-white"
        >
          <BackBtn />
          <p className="ml-6 text-[27px] text-textcolor-black">
            {parentI18n.t("MSG_SIGN_IN_FORGOT_PASSWORD_TITLE")}
          </p>
          {/* 言語選択ボタン - 右端に配置 */}
          <div className="ml-auto">
            <button
              ref={langBtnRef}
              onClick={handleToggleLocale}
              className="border-0 bg-green-100 flex-row-el items-center gap-2 px-4 py-2 rounded-full cursor-pointer transition-all w-[150px]"
            >
              <span className="material-symbols-outlined text-white !text-[20px]">
                language
              </span>
              <span className="text-body text-white text-[16px] flex-1 text-center">
                {localeLabels[currentLocale] || localeLabels["ja_kanji"]}
              </span>
              <span className="material-symbols-outlined text-white !text-[16px]">
                expand_more
              </span>
            </button>
          </div>
          <Select
            anchorRef={langBtnRef}
            open={localeOpen}
            onClose={handleCloseLocale}
            value={currentLocale}
            onChange={handleChangeLocale}
            options={[...localeOptions]}
          />
        </header>
      </div>

      {/* メインコンテンツ */}
      <div
        style={{ height: height - 90 * scale }}
        className="flex-col-el items-center justify-start w-full bg-white/40"
      >
        <main
          style={{
            width: width / scale,
            height: (height - 90 * scale) / scale,
            transform: `scale(${scale})`,
          }}
          className="flex-col-el flex-center shrink-0 p-6 origin-top overflow-y-auto"
        >
          <div className="bg-white rounded-[16px] p-8 max-w-md w-full">
            {!isEmailSent ? (
              <div className="flex-col-el flex-center w-full gap-4">
                <MobileInput
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setError("");
                    setEmail(e.target.value);
                  }}
                  placeholder={parentI18n.t("MSG_SIGN_USER_MAIL")}
                  label={parentI18n.t("MSG_SIGN_USER_MAIL")}
                  disabled={isLoading}
                />

                {error && (
                  <p className="text-[14px] text-danger text-center">{error}</p>
                )}

                <p className="text-[14px] text-center text-gray2-100">
                  {parentI18n.t("MSG_SIGN_PASSWORD_RESET_DESCRIPTION")}
                </p>

                <button
                  className="w-[375px] py-3.5 bg-green-100 text-white rounded-[12px] text-[18px] border-none cursor-pointer hover:opacity-90 transition-all disabled:opacity-50"
                  onClick={handlePasswordReset}
                  disabled={isLoading}
                >
                  {parentI18n.t("MSG_SIGN_PASSWORD_RESET_SEND_BTN")}
                </button>
              </div>
            ) : (
              <div className="flex-col-el flex-center gap-4 w-full">
                <div className="flex-row-el flex-center gap-2">
                  <span className="material-symbols-outlined text-[32px] text-green-100">
                    check_circle
                  </span>
                  <p className="text-[24px] text-textcolor-black">
                    {parentI18n.t("MSG_SIGN_EMAIL_SENT")}
                  </p>
                </div>

                <p className="text-[18px] text-textcolor-black break-all text-center">
                  {email}
                </p>

                <p className="text-[14px] text-gray2-100 text-center">
                  {parentI18n.t("MSG_SIGN_PASSWORD_RESET_SENT")}
                </p>

                <button
                  className="w-[375px] py-3.5 bg-green-100 text-white rounded-[12px] text-[18px] border-none cursor-pointer hover:opacity-90 transition-all mt-4"
                  onClick={handleBackToLogin}
                >
                  {parentI18n.t("MSG_SIGN_BACK_TO_LOGIN")}
                </button>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};
